/**
 * The project tree for synchronous React code, read through the Playground.
 *
 * `project.files` is only the root directory once directories are documents of
 * their own (a linked project, `playground/fs/dir-tree.ts`): nested entries
 * are in other documents a render can't walk. These hooks read through the
 * HAL instead and re-read whenever `watchTree` says the tree moved, so a
 * component sees nested files in either layout.
 */
import { isMountInode } from "playground/fs/mount";
import { isDirInode, isLinkedDir, type TreeEntry } from "playground/fs/paths";
import type { PlayFileInode } from "playground/fs/types";
import type { HostPlayground } from "playground/hal/index";
import { useEffect, useState } from "react";

/**
 * A counter that advances whenever the tree changes, coalesced to one bump per
 * macrotask (every body write stamps its inode, so changes arrive in bursts).
 * Use it as a dependency to re-derive anything read from the tree.
 */
export function useTreeRevision(playground: HostPlayground | null): number {
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!playground) return;
    let queued: ReturnType<typeof setTimeout> | undefined;
    const off = playground.watchTree(() => {
      queued ??= setTimeout(() => {
        queued = undefined;
        setRevision((r) => r + 1);
      }, 0);
    });
    return () => {
      off();
      if (queued) clearTimeout(queued);
    };
  }, [playground]);
  return revision;
}

/**
 * The project's own entries below `dir`: a mount is listed as its root and
 * not expanded, so no unit's manifest is fetched (and none can fail the
 * listing), and a linked directory already on the path isn't descended.
 */
async function ownEntries(
  playground: HostPlayground,
  dir: string,
  above: ReadonlySet<string>,
): Promise<TreeEntry[]> {
  const out: TreeEntry[] = [];
  for (const [name, inode] of Object.entries(await playground.listFiles(dir))) {
    const path = dir ? `${dir}/${name}` : name;
    const isDirectory = isDirInode(inode);
    out.push({ path, inode, isDirectory });
    if (!isDirectory || isMountInode(inode) || above.has(inode.uri)) continue;
    const next = isLinkedDir(inode) ? new Set([...above, inode.uri]) : above;
    out.push(...(await ownEntries(playground, path, next)));
  }
  return out;
}

/**
 * Every entry the project itself holds (mounts unexpanded, as `project.files`
 * always showed them), or `undefined` until read.
 */
export function useTreeEntries(
  playground: HostPlayground | null,
): TreeEntry[] | undefined {
  const revision = useTreeRevision(playground);
  const [entries, setEntries] = useState<TreeEntry[]>();
  useEffect(() => {
    if (!playground) return;
    let live = true;
    void ownEntries(playground, "", new Set()).then(
      (next) => live && setEntries(next),
    );
    return () => {
      live = false;
    };
  }, [playground, revision]);
  return entries;
}

/**
 * The inode at exactly `path` (`HostPlayground.stat`): `undefined` until read,
 * `null` when nothing is there.
 */
export function useInodeAt(
  playground: HostPlayground | null,
  path: string | undefined,
): PlayFileInode | null | undefined {
  const revision = useTreeRevision(playground);
  // Tagged with its path, so a new path never shows the last one's inode.
  const [read, setRead] = useState<{
    path: string;
    inode: PlayFileInode | null;
  }>();
  useEffect(() => {
    if (!playground || path === undefined) return;
    let live = true;
    void playground
      .stat(path)
      .then((inode) => live && setRead({ path, inode }));
    return () => {
      live = false;
    };
  }, [playground, path, revision]);
  return read && read.path === path ? read.inode : undefined;
}
